import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { BadgeInput } from '@neo/blocks/input-with-badge'
import { Tooltip } from '@neo/primitives/tooltip'
import { InfoIcon } from 'lucide-react'
import { useEffect } from 'react'
import { websiteScraperSchema } from '../../config'
import type { NewKBFormValues } from '../../types'

export const WebsiteScraperOptions = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  // 'all' = "sync entire source": sub-page discovery is always on, so the "Crawl sub-pages" checkbox
  // is hidden there (showing an effectively-forced toggle misled users about which limits applied).
  const isAll = form.useStore((s) => s.values.url_selection_type === 'all')
  // Sub-page discovery is implied by both 'selected' (we crawl to list pages to pick) and 'all'
  // ("sync entire source" = crawl the whole site). Forcing crawl_subpages=true also enables the
  // Limit / Max depth inputs (they're disabled when crawl_subpages is false) so the user can tune
  // the crawl in BOTH modes instead of being silently capped at the seed page.
  useEffect(() => {
    const urlSelectionType = form.state.values.url_selection_type
    if (urlSelectionType === 'selected' || urlSelectionType === 'all') {
      form.setFieldValue('crawl_subpages', true)
      // Trigger validation to update dependent fields
      form.validateField('crawl_subpages', 'change')
    }
  }, [form, form.state.values.url_selection_type])
  const crawlSubpagesField: GenericField<NewKBFormValues>[] = isAll
    ? [] // hidden in 'all' mode (sub-page discovery is implied + forced on)
    : [
        {
          name: 'crawl_subpages',
          className: 'col-span-2 mb-2',
          componentType: 'checkbox',
          zodValidation: websiteScraperSchema.shape.crawl_subpages,
          inputProps: {
            label: 'Crawl sub-pages',
            'data-testid': 'switch-crawl-subpages',
          },
        },
      ]
  return (
    <div className="grid grid-cols-2 gap-x-2 gap-y-4 px-0.5">
      <FieldsRenderer<NewKBFormValues>
        form={form}
        fields={[
          ...crawlSubpagesField,
          {
            name: 'max_pages',
            componentType: 'inputNumber',
            className: 'col-span-1',
            disabled: (values: NewKBFormValues) => values.crawl_subpages === false,
            onChangeListenTo: ['crawl_subpages'],
            zodValidation: websiteScraperSchema.shape.max_pages,
            inputProps: {
              label: (
                <div className="flex items-center">
                  <span>Limit</span>
                  <span className="text-p-400 text-destructive mr-1">*</span>
                  <Tooltip content="Set the limit for the total number of sub-pages">
                    <InfoIcon size={12} />
                  </Tooltip>
                </div>
              ),
              placeholder: 'Enter limit',
              type: 'number',
              min: 1,
              max: 2000,
              'data-testid': 'input-max-pages',
            },
          },
          {
            name: 'max_depth',
            className: 'col-span-1',
            disabled: (values: NewKBFormValues) => values.crawl_subpages === false,
            onChangeListenTo: ['crawl_subpages'],
            componentType: 'inputNumber',
            zodValidation: websiteScraperSchema.shape.max_depth,
            inputProps: {
              label: (
                <div className="flex items-center">
                  <span>Max depth</span>
                  <span className="text-p-400 text-destructive mr-1">*</span>
                  <Tooltip content="Set the max depth relative to the entered URL (Depth 0 scrapes only the entered URL, while depth 1 scrapes the URL and its immediate sub-pages)">
                    <InfoIcon size={12} />
                  </Tooltip>
                </div>
              ),
              placeholder: 'Enter max depth',
              type: 'number',
              min: 1,
              max: 30,
              'data-testid': 'input-max-depth',
            },
          },
          {
            name: 'exclude_paths',
            className: 'col-span-1',
            disabled: (values: NewKBFormValues) => values.crawl_subpages === false,
            onChangeListenTo: ['crawl_subpages'],
            type: 'custom',
            component: ({ onChange, value, ...props }) => (
              <BadgeInput {...props} onChange={onChange} value={value as string[]} />
            ),
            valueFormatter: (value: unknown) => (Array.isArray(value) ? value : []),
            zodValidation: websiteScraperSchema.shape.exclude_paths,
            eventDataExtractor: (v) => v,
            inputProps: {
              label: 'Exclude paths',
              placeholder: 'blog/*, about/*',
              'data-testid': 'input-exclude-paths',
            },
          },
          {
            name: 'include_paths',
            disabled: (values: NewKBFormValues) => values.crawl_subpages === false,
            onChangeListenTo: ['crawl_subpages'],
            type: 'custom',
            valueFormatter: (value: unknown) => (Array.isArray(value) ? value : []),
            eventDataExtractor: (v) => v,
            component: ({ onChange, value, ...props }) => (
              <BadgeInput {...props} onChange={onChange} value={value as string[]} />
            ),
            className: 'col-span-1',
            zodValidation: websiteScraperSchema.shape.include_paths,
            inputProps: {
              label: 'Include only paths',
              placeholder: 'articles/*',
              'data-testid': 'input-include-paths',
            },
          },
        ]}
      />
    </div>
  )
}
